import moment from 'moment'
import React, { useState } from 'react'
import { Button, DropdownProps, Form, Grid, Icon, Image, Label, ListItemProps, Segment } from 'semantic-ui-react'
import { HtmlLabelProps } from 'semantic-ui-react/dist/commonjs/generic'
import clone from '../../../../assets/svg/clone_entry.svg'
import manual from '../../../../assets/svg/manual_entry.svg'
import select_icon from '../../../../assets/svg/modal_select_icon.svg'
import unselect_icon from '../../../../assets/svg/modal_unselected_icon.svg'
import DropdownComponent from '../../../../components/Dropdown/DropdownComponent'
import DropdownInsideScrollableParent from '../../../../components/Dropdown/DropdownInsideScrollableParent'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import ValidationInput from '../../../../components/ValidationInput/ValidationInput'
import { IUserDetail } from '../../../../types/user'
import { initialDepartments } from '../../../settings/reducers/departments'
import { IDepartment } from '../../../settings/types/department'
import { initialWizardAct, initialwizardProjectPhase } from '../../reducers/wizardReducer'
import { IProject, IProjectPhase } from '../../types/project'
import { IProjectActivity } from '../../types/projectActivity'
import { IWizardProject, IWizardProjectActivity } from '../../types/wizardData'
import { cloneSourceProjects, toCloneProjectOptions, useCloneSourceProjects } from './useCloneSourceProjects'

interface Props {
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
  wizardCreateProject: (project: IWizardProject) => void
  departments: IDepartment[]
  user: IUserDetail | null
  getActs: (projectId: number) => Promise<IProjectActivity[]>
  activities: IProjectActivity[]
  projectClonedState?: boolean
  clonedTasksList?: string[]
}

const options = [
  { id: 1, name: 'Manual entry', type: 'manual', svg: manual },
  { id: 2, name: 'Clone existing project', type: 'clone', svg: clone },
]

const maxLength25ValidationRule = new RegExp('^.{0,25}$')
const invalidMaxLength25ValidationRuleMsg = 'Max number of characters exceeded. There are only 25 chars allowed.'
const maxLength26 = 26

const WizardEnterActivities = (props: Props): JSX.Element => {
  const [activitiesList, setActivitiesList] = useState<IWizardProjectActivity[]>(
    props.data.activities.length !== 0 ? props.data.activities : [{ ...initialWizardAct, id: '0' }]
  )
  const [selectedInputOption, setSelectedInputOption] = useState(props.data.input_option)
  const [selectedProject, setSelectedProject] = useState<IProject | undefined>(undefined)
  const projects = useCloneSourceProjects(selectedInputOption === 'clone')
  const [inputValues, setInputValues] = useState<string[]>(
    props.data.activities.length !== 0 ? props.data.activities.map((a) => a.total_hours.toString()) : ['']
  )
  const [projectCloned, setProjectCloned] = useState(props.data.projectClonedState ? props.data.projectClonedState : false)
  const [clonedTasks, setClonedTasks] = useState<string[]>(props.data.clonedTasksList ? props.data.clonedTasksList : [])
  const [inputFocusedId, setInputFocusedId] = useState<string>('')

  const onChange = (
    e: React.SyntheticEvent<HTMLElement, Event>,
    data: ListItemProps | HtmlLabelProps,
    activityId: string | number,
    dropdown: string
  ) => {
    const _acts = activitiesList.map((a) => {
      if (a.id === activityId) {
        if (dropdown === 'phase') {
          a.phase = props.data.phases.filter((p) => p.name === data.value)[0]
        } else if (dropdown === 'department') {
          a.department = props.departments.filter((p) => p.name === data.value)[0]
        }
      }
      return a
    })
    setActivitiesList(_acts)
  }

  const renderPhaseLabel = (phase: IProjectPhase) => {
    const _upperCase = phase.name.toUpperCase() === phase.name ? true : false
    const _nameLen = _upperCase
      ? phase.name.length > 15
        ? phase.name.slice(0, 14).concat('...')
        : phase.name
      : phase.name.length > 19
        ? phase.name.slice(0, 17).concat('...')
        : phase.name
    return (
      <Label
        key={phase.id}
        horizontal
        style={{
          borderRadius: '.6rem',
          color: phase.color,
          backgroundColor: phase.color + '33',
          fontSize: '.8rem',
          height: '1.7rem',
          minWidth: 'auto',
        }}
        content={_nameLen}
      />
    )
  }

  const renderPhasePopup = (activityId: string | number, phase: IProjectPhase, idx: number) => {
    const _activity = activitiesList.filter((a) => a.id === activityId)[0]
    const _options = props.data.phases
      .map((p) => {
        // add opacity 30% to phase color
        const _lightColor = p.color + '33'
        const _upperCase = p.name.toUpperCase() === p.name ? true : false
        const _nameLen = _upperCase
          ? p.name.length > 17
            ? p.name.slice(0, 16).concat('...')
            : p.name
          : p.name.length > 21
            ? p.name.slice(0, 20).concat('...')
            : p.name
        return {
          key: p.id,
          text: (
            <Label
              key={p.id}
              horizontal
              style={{
                color: p.color,
                backgroundColor: _lightColor,
                fontSize: '.8rem',
                height: '1.7rem',
                borderRadius: '.6rem',
                whiteSpace: 'nowrap'
              }
              }
              content={_nameLen}
            />
          ),
          value: p.name,
          selected: _activity.phase.name === p.name,
        }
      })
      .sort((a, b) => a.value.localeCompare(b.value))
    return (
      <Form.Field width={4} style={{ paddingRight: 0, whiteSpace: 'nowrap' }} key={`${idx}-phase`}>
        {renderLabels('Phase', 2, idx)}
        <DropdownInsideScrollableParent
          options={_options}
          content={renderPhaseLabel(phase)}
          iconFontSize='1.2rem'
          iconName='caret down'
          name={phase.name}
          iconColor='#08D6A1'
          placeholder='Add phase'
          marginTop={idx === 0 ? '2rem' : ''}
          width={activitiesList.length > 5 ? '12rem' : '12.1rem'}
          buttonWidth='12rem'
          onClick={(e, data) => onChange(e, data, activityId, 'phase')}
        />
      </Form.Field>
    )
  }

  const renderDepartmentPopup = (activityId: string | number, department: IDepartment, idx: number) => {
    const _activity = activitiesList.filter((a) => a.id === activityId)[0]
    const _options = props.departments.map((d) => {
      const _upperCase = d.name.toUpperCase() === d.name ? true : false
      const _name = _upperCase
        ? d.name.length > 15
          ? d.name.slice(0, 14).concat('...')
          : d.name
        : d.name.length > 19
          ? d.name.slice(0, 16).concat('...')
          : d.name
      return {
        key: d.id,
        text: _name,
        value: d.name,
        selected: _activity.department.name === d.name,
      }
    })
    const _upperCase = department.name.toUpperCase() === department.name ? true : false
    const _name = _upperCase
      ? department.name.length > 14
        ? department.name.slice(0, 13).concat('...')
        : department.name
      : department.name.length > 18
        ? department.name.slice(0, 15).concat('...')
        : department.name
    return (
      <Form.Field width={4} style={{ paddingRight: 0 }} key={`${idx}-department`}>
        {renderLabels('Department', 3, idx)}
        <DropdownInsideScrollableParent
          options={_options}
          content={_name}
          iconFontSize='1.2rem'
          iconName='caret down'
          iconColor='#08D6A1'
          placeholder='Add department'
          marginTop={idx === 0 ? '2rem' : ''}
          width={activitiesList.length > 5 ? '12.1rem' : '12rem'}
          buttonWidth='12rem'
          onClick={(e, data) => onChange(e, data, activityId, 'department')}
        />
      </Form.Field>
    )
  }

  const onClick = (item: string) => {
    setSelectedInputOption(item)
  }

  const renderInputOptions = () => {
    return (
      <Grid.Row columns={2} style={{ paddingTop: '.3rem' }}>
        {options.map((o) => {
          return (
            <Grid.Column key={o.id}>
              <Segment
                onClick={() => onClick(o.type)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  opacity: o.type === selectedInputOption ? 1 : 0.6,
                  border: `2px solid ${o.type === selectedInputOption ? '#08D6A1' : '#BEC8C3'}`,
                  backgroundColor: 'transparent',
                  paddingTop: '16px',
                  display: 'flex',
                  color: '#232C58',
                  height: '54px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'left',
                    width: '90%',
                    fontWeight: 'bold',
                  }}
                >
                  <Image style={{ marginLeft: '18px', marginRight: '17px', height: '18.5px', marginBottom: '.2rem' }} src={o.svg} />
                  {o.name}
                </div>
                <Image
                  style={{ height: '18.5px' }}
                  src={o.type === selectedInputOption ? select_icon : unselect_icon}
                />
              </Segment>
            </Grid.Column>
          )
        })}
      </Grid.Row>
    )
  }

  const inputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: string | number,
    inputName: string,
    idx: number,
    converted?: number
  ) => {
    const _activities = activitiesList.map((a) => {
      if (a.id === key) {
        if (inputName === 'task') {
          a.name = event.target.value
        } else if (inputName === 'targetHours') {
          a.total_hours = converted ?? 0
        }
      }
      return a
    })
    setActivitiesList(_activities)
    if (inputName === 'targetHours') {
      const _values = inputValues.map((iv, i) => {
        if (idx === i) {
          return event.target.value
        }
        return iv
      })
      setInputValues(_values)
    }
  }

  const renderTaskInput = (item: IWizardProjectActivity, idx: number) => {
    return (
      <ValidationInput
        fluid
        value={item.name}
        onChange={(e) => inputChange(e, item.id, 'task', idx)}
        placeholder='Enter Task'
        fieldWidth={7}
        label={renderLabels('Task', 1, idx)}
        paddingRight='.2rem'
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        validatePattern={maxLength25ValidationRule}
        invalidMessage={invalidMaxLength25ValidationRuleMsg}
        maxLength={maxLength26}
        
      />
    )
  }

  const renderTargetHoursInput = (item: IWizardProjectActivity, idx: number) => {
    return (
      <ValidationInput
        fluid
        type='number'
        wholeNumber={false}
        value={inputValues[idx]}
        onChange={(e, v, c) => inputChange(e, item.id, 'targetHours', idx, c)}
        placeholder='Add hours'
        fieldWidth={3}
        label={renderLabels('Target Hours', 4, idx)}
        paddingRight={0}
        marginTop={idx === 0 ? '2rem' : ''}
        zIndex={1}
        symbol='h'
        boldSymbol={true}
        twoDecimalPlaces
      />
    )
  }

  const removeActivity = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    item: IWizardProjectActivity,
    idx: number
  ) => {
    const list = activitiesList.filter((i) => i.id !== item.id)
    const clonedTasksList = clonedTasks.filter((i: string) => i !== item.name)
    const cloneActive = clonedTasksList.length > 0
    setActivitiesList(list)
    setInputValues(inputValues.filter((iv, i) => i !== idx))
    setClonedTasks(clonedTasksList)
    setProjectCloned(cloneActive)
  }

  const renderRemoveIcon = (item: IWizardProjectActivity, idx: number) => {
    if (item.id !== '') {
      return (
        <Button
          key={idx}
          icon
          onClick={(e) => removeActivity(e, item, idx)}
          style={{ backgroundColor: 'transparent', boxShadow: 'none', marginTop: idx === 0 ? '1.6rem' : '' }}
        >
          <Icon name='times circle' color='red' style={{ fontSize: '15px' }} />
        </Button>
      )
    }
    return null
  }

  const handleOnChangeProject = (e: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    if (data && data.value) {
      const _val: number = data.value as number
      projects.forEach((item) => {
        if (+item.id === +_val) {
          setSelectedProject(item)
        }
      })
    }
  }

  const handleAddTasks = () => {
    if (selectedProject) {
      props.getActs(+selectedProject.id).then((resp) => {
        const key = activitiesList.length !== 0 ? Math.max(...activitiesList.map(d => +d.id)) + 1 : 0
        const _acts: IWizardProjectActivity[] = resp.map((a, idx) => {
          clonedTasks.push(a.name)
          setClonedTasks(clonedTasks)
          return {
            id: (key + idx).toString(),
            name: a.name,
            phase: initialwizardProjectPhase,
            department: a.department ? a.department : initialDepartments[0],
            start_date: a.start_date,
            end_date: a.end_date,
            current_hours: 0,
            total_hours: a.total_hours,
            status: 'upcoming',
          }
        })
        setActivitiesList([...activitiesList.filter((a) => a.id !== '0' || a.name !== ''), ..._acts])
        setInputValues([
          ...activitiesList.filter((a) => a.id !== '0' || a.name !== '').map((a) => a.total_hours.toString()),
          ..._acts.map((a) => a.total_hours.toString()),
        ])
      })
      setProjectCloned(true)
    }
  }

  const renderCloneTaskForm = () => {
    if (props.user) {
      const _options = toCloneProjectOptions(cloneSourceProjects(projects, props.user, false), selectedProject?.id)
      return (
        <Grid.Row columns={2} style={{ paddingTop: '.3rem' }}>
          <Form style={{ width: '100%', paddingLeft: '1rem' }}>
            <Form.Group widths='equal'>
              <Form.Field>
                <DropdownComponent
                  search={true}
                  title='Projects'
                  width='auto'
                  changeOption={handleOnChangeProject}
                  options={_options}
                  placeholder='Select project'
                  value={selectedProject?.id}
                  disabledDropdown={_options.length === 0 || projectCloned}
                  icon={ <Icon name='caret down' style={{color: '#08D6A1', fontSize: '1.2rem', position: 'fixed', right: '5px', zIndex: 2, cursor: 'pointer' }}/>}
                />
              </Form.Field>
              <Form.Field>
                <Button
                  disabled={!selectedProject || projectCloned}
                  primary={true}
                  size='small'
                  onClick={handleAddTasks}
                  content='Clone Project'
                  style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
                />
              </Form.Field>
            </Form.Group>
          </Form>
        </Grid.Row>
      )
    }
    return null
  }

  const renderTaskForm = () => {
    return activitiesList.map((i, idx) => {
      return (
        <Form.Group key={i.id}>
          {renderTaskInput(i, idx)}
          {renderPhasePopup(i.id, i.phase, idx)}
          {renderDepartmentPopup(i.id, i.department, idx)}
          {renderTargetHoursInput(i, idx)}
          {renderRemoveIcon(i, idx)}
        </Form.Group>
      )
    })
  }

  const renderLabels = (label: string, key: number, idx: number) => {
    if (idx === 0) {
      return (
        <label
          key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'fixed',
            zIndex: activitiesList.length + 1,
            width: 'inherit',
            backgroundColor: 'white',
          }}
        >
          <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, color: '#172533' }}>{label}</p>
        </label>
      )
    }
    return null
  }

  const addTask = () => {
    let key = 1
    if (activitiesList.length !== 0) {
      key = Math.max(...activitiesList.map((a) => +a.id)) + 1
    }

    const act: IWizardProjectActivity = {
      ...initialWizardAct,
      id: key.toString(),
    }
    setActivitiesList([...activitiesList, act])
    setInputValues([...inputValues, ''])
  }

  const renderContent = () => {
    return (
      <div>
        <Grid>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <p style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#172533' }}>
              Input Options
            </p>
          </label>
          {renderInputOptions()}
        </Grid>
        {selectedInputOption === 'clone' ? (
          <Grid style={{ marginTop: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <p
                style={{ fontSize: '1.28rem', fontWeight: 'bold', margin: 0, letterSpacing: '.25px', color: '#232C58' }}
              >
                Select Project
              </p>
            </label>
            {renderCloneTaskForm()}
          </Grid>
        ) : null}
        <div style={{ marginTop: '1rem' }}>
          <div
            onScroll={() => setInputFocusedId('')}
            style={
              activitiesList.length > 5
                ? { overflowY: 'auto', maxHeight: '20.57rem', scrollbarWidth: 'thin' }
                : undefined
            }
          >
            <Form
              style={{
                margin: activitiesList.length > 5 ? 0 : 'auto',
                width: activitiesList.length > 5 ? '98%' : '100%',
              }}
            >
              {renderTaskForm()}
            </Form>
          </div>
          <Button
            disabled={selectedInputOption === ''}
            className='button-add-item'
            inverted
            size='small'
            color='teal'
            onClick={() => addTask()}
            content='Add Task'
            icon={<Icon name='plus' />}
            style={{
              width: '8.57rem',
              height: '2.14rem',
              borderRadius: '.34rem',
              padding: '0rem',
              color: '#08D6A1',
              backgroundColor: '#CDF7EC',
            }}
          />
        </div>
      </div>
    )
  }

  const disableNext = () => {
    return !(
      activitiesList.length > 0 &&
      activitiesList.every(
        (act) =>
          act.name !== '' &&
          act.name.length < maxLength26 &&
          act.phase.id !== '' &&
          act.phase.id !== -1 &&
          act.department.id !== '' &&
          act.total_hours !== 0
      )
    )
  }

  const renderFooter = () => {
    return (
      <div>
        <Button
          inverted
          color='grey'
          size='small'
          onClick={goToPreviousStep}
          content='Back'
          style={{
            width: '8.57rem',
            height: '2.14rem',
            borderRadius: '.34rem',
            padding: 0,
            marginRight: '1rem',
            color: '#747678',
            borderColor: '#BEC8C3',
          }}
        />
        <Button
          disabled={disableNext()}
          primary={true}
          size='small'
          onClick={onClickToFinish}
          content='Finish'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
        />
      </div>
    )
  }

  const onClickToFinish = () => {
    const acts = activitiesList
      .filter((a) => a.name !== '')
      .map((a) => {
        return {
          ...a,
          id: '',
          description: null,
          start_date: a.phase.start_date ? moment(a.phase.start_date).format('YYYY-MM-DD') : null,
          end_date: a.phase.end_date ? moment(a.phase.end_date).format('YYYY-MM-DD') : null,
        }
      })

    const _project: IWizardProject = {
      ...props.data,
      employees: props.data.permissions === 'open' ? [] : props.data.employees,
      activities: acts,
      projectClonedState: projectCloned,
      clonedTasksList: clonedTasks,
      phases: props.data.phases.map((phase) => {
        return {
          ...phase,
          start_date: phase.start_date ? moment(phase.start_date).format('YYYY-MM-DD') : '',
          end_date: phase.end_date ? moment(phase.end_date).format('YYYY-MM-DD') : '',
        }
      }),
    }
    delete _project.input_option
    props.setProjectWizardData(_project)
    props.wizardCreateProject(_project)
  }

  const goToPreviousStep = () => {
    const _data: IWizardProject = {
      ...props.data,
      activities: activitiesList,
      input_option: selectedInputOption,
      projectClonedState: projectCloned,
      clonedTasksList: clonedTasks,
    }
    props.setWizardStep('step-four')
    props.setProjectWizardData(_data)
  }

  const renderWizard = () => {
    const _data: IWizardProject = {
      ...props.data,
      activities: activitiesList,
      input_option: selectedInputOption,
      projectClonedState: projectCloned,
      clonedTasksList: clonedTasks
    }
    const _steps = [
      { key: 1, name: 'step-one' },
      { key: 2, name: 'step-two' },
      { key: 3, name: 'step-three' },
      { key: 4, name: 'step-four' },
      { key: 5, name: 'step-five' },
      { key: 6, name: 'step-six' },
    ]
    return (
      <WizardComponent
        open={props.open}
        close={props.onClose}
        content={renderContent()}
        headerContent='Select Task and Duties'
        headerColor='#232C58'
        footer={renderFooter()}
        steps={5}
        activeStep={5}
        width={'930px'}
        finishedSteps={props.wizardFinishedSteps}
        setWizardStep={props.setWizardStep}
        setProjectWizardData={props.setProjectWizardData}
        data={_data}
        stepStringList={_steps}
        headerContentFontSize='28px'
      />
    )
  }

  return renderWizard()
}

export default WizardEnterActivities
